/**
 * The `…` actions menu (spec.md § UI & design / Secondary actions).
 *
 * Built on Base UI `Menu.Root` / `Menu.Trigger` / `Menu.Popup` so keyboard
 * navigation (arrow keys, Enter/Space to open, Escape/outside-click to close)
 * and ARIA come for free — we do not strip them. Items:
 *   - Copy as markdown (with ⌘C hint)
 *   - Copy link
 *   - divider
 *   - Delete pad (destructive red)
 *
 * The menu opens with a subtle fade + scale from the top-right; that animation
 * is disabled under `prefers-reduced-motion` via the global rule in
 * styles/index.css. Actions are passed in as callbacks so this component stays
 * presentational — the route wires them to the api client and toasts.
 */

import { Menu } from "@base-ui-components/react/menu";
import { Copy, Link2, MoreHorizontal, Trash2 } from "lucide-react";

const ITEM_CLASS =
  "flex w-full cursor-default items-center justify-between gap-6 rounded-[7px] px-3 py-2 font-mono text-[13px] text-text outline-none select-none data-[highlighted]:bg-sunken";

export interface ActionsMenuProps {
  onCopyMarkdown: () => void;
  onCopyLink: () => void;
  onDelete: () => void;
}

export function ActionsMenu({
  onCopyMarkdown,
  onCopyLink,
  onDelete,
}: ActionsMenuProps): React.JSX.Element {
  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label="More actions"
        className="flex size-9 items-center justify-center rounded-[9px] border border-line text-text-2 outline-none transition-colors hover:bg-sunken hover:text-text focus-visible:ring-3 focus-visible:ring-accent/40 active:translate-y-px"
      >
        <MoreHorizontal aria-hidden="true" className="size-4" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner align="end" side="bottom" sideOffset={8} className="z-50">
          <Menu.Popup className="min-w-52 origin-top-right rounded-[13px] border border-line bg-raised p-1.5 shadow-lg outline-none transition-[transform,opacity] duration-150 ease-out data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0">
            <Menu.Item className={ITEM_CLASS} onClick={onCopyMarkdown}>
              <span className="flex items-center gap-2.5">
                <Copy aria-hidden="true" className="size-4 text-text-2" />
                Copy as markdown
              </span>
              <kbd className="font-mono text-[11px] text-text-3">⌘C</kbd>
            </Menu.Item>
            <Menu.Item className={ITEM_CLASS} onClick={onCopyLink}>
              <span className="flex items-center gap-2.5">
                <Link2 aria-hidden="true" className="size-4 text-text-2" />
                Copy link
              </span>
            </Menu.Item>
            <Menu.Separator className="my-1.5 h-px bg-line" />
            <Menu.Item
              className="flex w-full cursor-default items-center gap-2.5 rounded-[7px] px-3 py-2 font-mono text-[13px] text-danger outline-none select-none data-[highlighted]:bg-danger/10"
              onClick={onDelete}
            >
              <Trash2 aria-hidden="true" className="size-4" />
              Delete pad
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
